import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Server, User, ClipboardCheck, Check, X, Loader2,
  ArrowRight, ArrowLeft, MailCheck,
} from 'lucide-react';
import { onboardingApi } from '../services/api';
import AfricaNendaLogo from '../components/AfricaNendaLogo';
import { TERMS_VERSION } from '../data/terms';

const CORRIDORS = ['FSPIOP', 'ISO20022', 'ISO8583'];
const STEPS = [
  { name: 'Organisation', icon: Building2 },
  { name: 'Technical',    icon: Server },
  { name: 'Contact',      icon: User },
  { name: 'Review',       icon: ClipboardCheck },
];

const Shell = ({ children, wide }) => (
  <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-100 flex items-center justify-center p-4">
    <div className={wide ? 'max-w-xl w-full' : 'max-w-md w-full'}>
      <div className="text-center mb-6">
        <AfricaNendaLogo className="h-10 mx-auto mb-3" />
        <h1 className="text-2xl font-bold text-gray-900">Join the AfricaNenda IIPS Sandbox</h1>
        <p className="text-sm text-gray-500 mt-1">Apply to onboard as a participant. AfricaNenda reviews every application.</p>
      </div>
      <div className="bg-white rounded-xl shadow-xl p-8">{children}</div>
      <p className="text-center text-xs text-gray-500 mt-4">
        Already have an account? <Link to="/login" className="text-primary-600 font-medium">Sign in</Link>
      </p>
    </div>
  </div>
);

function Input({ label, value, onChange, placeholder, type = 'text', hint }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
      {hint && <span className="text-xs text-gray-400 mt-0.5 block">{hint}</span>}
    </label>
  );
}

export default function Register() {
  const [step, setStep] = useState(0);
  const [f, setF] = useState({
    orgLegalName: '', displayName: '', country: '', website: '',
    proposedTenantId: '', proposedFspId: '', corridors: ['FSPIOP', 'ISO20022'], intendedUse: '',
    contactName: '', contactEmail: '', termsAccepted: false,
  });
  const [avail, setAvail]           = useState(null);
  const [checking, setChecking]     = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]             = useState(null);
  const [error, setError]           = useState(null);

  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const toggleCorridor = (c) => set('corridors', f.corridors.includes(c) ? f.corridors.filter(x => x !== c) : [...f.corridors, c]);

  // Debounced live availability of the proposed identifiers.
  useEffect(() => {
    if (step !== 1 || !f.proposedTenantId) { setAvail(null); return; }
    setChecking(true);
    const t = setTimeout(() => {
      onboardingApi.availability(f.proposedTenantId, f.proposedFspId)
        .then(setAvail).catch(() => setAvail(null)).finally(() => setChecking(false));
    }, 400);
    return () => clearTimeout(t);
  }, [f.proposedTenantId, f.proposedFspId, step]);

  const canNext = () => {
    if (step === 0) return f.orgLegalName && f.displayName;
    if (step === 1) return avail?.tenantIdAvailable && (!f.proposedFspId || avail?.fspIdAvailable) && f.corridors.length > 0;
    if (step === 2) return f.contactName && /.+@.+\..+/.test(f.contactEmail);
    return f.termsAccepted;
  };

  const submit = async () => {
    setSubmitting(true); setError(null);
    try { setDone(await onboardingApi.submit({ ...f, termsVersion: TERMS_VERSION })); }
    catch (e) { setError(e.response?.data?.error || e.response?.data?.message || e.message); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <Shell>
        <div className="text-center">
          <div className="inline-flex w-14 h-14 bg-green-100 rounded-full items-center justify-center mb-4">
            <MailCheck className="w-7 h-7 text-green-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Application submitted</h2>
          <p className="text-sm text-gray-600 mt-2">
            We've emailed <span className="font-medium">{f.contactEmail}</span> a link to verify your address.
            Verify it, and AfricaNenda will review your application.
          </p>
          <Link to={`/register/status/${done.statusToken}`}
            className="inline-block mt-5 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700">
            Track your application
          </Link>
        </div>
      </Shell>
    );
  }

  const availIcon = (ok) => ok
    ? <span className="inline-flex items-center gap-1 text-xs text-green-600"><Check className="w-3.5 h-3.5" /> available</span>
    : <span className="inline-flex items-center gap-1 text-xs text-red-500"><X className="w-3.5 h-3.5" /> unavailable</span>;

  return (
    <Shell wide>
      {/* progress */}
      <div className="flex items-center justify-between mb-6">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const active = i === step, doneStep = i < step;
          return (
            <React.Fragment key={s.name}>
              <div className="flex flex-col items-center">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                  active ? 'bg-primary-600 text-white' : doneStep ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                  {doneStep ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                </div>
                <span className={`text-xs mt-1 ${active ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>{s.name}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${i < step ? 'bg-green-300' : 'bg-gray-200'}`} />}
            </React.Fragment>
          );
        })}
      </div>

      {error && <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>}

      {/* step content */}
      <div className="space-y-4">
        {step === 0 && (<>
          <Input label="Organisation legal name" value={f.orgLegalName} onChange={v => set('orgLegalName', v)} placeholder="Acme Payments Ltd" />
          <Input label="Display name" value={f.displayName} onChange={v => set('displayName', v)} placeholder="Acme Payments" hint="Shown in dashboards and reports." />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Country" value={f.country} onChange={v => set('country', v.toUpperCase().slice(0, 2))} placeholder="RW" hint="2-letter code" />
            <Input label="Website" value={f.website} onChange={v => set('website', v)} placeholder="https://acme.example" />
          </div>
        </>)}

        {step === 1 && (<>
          <div>
            <Input label="Proposed tenant id" value={f.proposedTenantId}
              onChange={v => set('proposedTenantId', v.toLowerCase())} placeholder="acme-rw"
              hint="Lowercase letters, numbers and hyphens (3–50 chars)." />
            <div className="mt-1 h-4">
              {checking ? <span className="text-xs text-gray-400 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> checking…</span>
                : avail && f.proposedTenantId ? (avail.tenantIdValid ? availIcon(avail.tenantIdAvailable) : <span className="text-xs text-red-500">invalid format</span>) : null}
            </div>
          </div>
          <div>
            <Input label="Proposed FSP id" value={f.proposedFspId} onChange={v => set('proposedFspId', v)} placeholder="AcmeFSP"
              hint="Your Mojaloop FSP identifier." />
            <div className="mt-1 h-4">{avail && f.proposedFspId ? availIcon(avail.fspIdAvailable) : null}</div>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-700">Corridors</span>
            <div className="flex gap-4 mt-1.5">
              {CORRIDORS.map(c => (
                <label key={c} className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={f.corridors.includes(c)} onChange={() => toggleCorridor(c)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  {c}
                </label>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Intended use <span className="text-gray-400 font-normal">(optional)</span></span>
            <textarea value={f.intendedUse} onChange={e => set('intendedUse', e.target.value)} rows={2}
              placeholder="What you plan to test or run"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-primary-500" />
          </label>
        </>)}

        {step === 2 && (<>
          <Input label="Primary contact name" value={f.contactName} onChange={v => set('contactName', v)} placeholder="Jane Doe" />
          <Input label="Contact email" type="email" value={f.contactEmail} onChange={v => set('contactEmail', v)} placeholder="jane@acme.example"
            hint="You'll verify this address. It also becomes your username when you sign in." />
        </>)}

        {step === 3 && (<>
          <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-3 text-sm">
            {[
              ['Organisation', f.orgLegalName], ['Display name', f.displayName], ['Country', f.country],
              ['Tenant id', f.proposedTenantId], ['FSP id', f.proposedFspId], ['Corridors', f.corridors.join(', ')],
              ['Contact', f.contactName], ['Email', f.contactEmail],
            ].map(([k, v]) => (
              <div key={k}><p className="text-xs text-gray-500">{k}</p><p className="text-gray-900 font-medium break-words">{v || '—'}</p></div>
            ))}
          </div>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={f.termsAccepted} onChange={e => set('termsAccepted', e.target.checked)}
              className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
            <span>
              I accept the Sandbox{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary-600 font-medium">terms of service</a>,
              including <a href="/terms#data" target="_blank" rel="noopener noreferrer" className="text-primary-600 font-medium">how AfricaNenda handles data</a>{' '}
              <span className="text-gray-400">({TERMS_VERSION})</span>.
            </span>
          </label>
        </>)}
      </div>

      {/* nav */}
      <div className="flex items-center justify-between mt-6">
        <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0}
          className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 disabled:opacity-40">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        {step < 3 ? (
          <button onClick={() => setStep(s => s + 1)} disabled={!canNext()}
            className="flex items-center gap-1 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-40">
            Next <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={submit} disabled={!canNext() || submitting}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-40">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Submit application
          </button>
        )}
      </div>
    </Shell>
  );
}
